import type { FlavourProfilesProps } from '@dcxwam/dcx-wam-ui-foundations/build/types/components/organisms/product/common/flavour-profiles'

import type { ShopConfig } from './config'

const FLAVOUR_SCALE = 5

const FLAVOUR_KEYS = ['intensity', 'aroma', 'body', 'cooling'] as const
type FlavourKey = (typeof FLAVOUR_KEYS)[number]

type FlavourLabels = Pick<
  ShopConfig,
  'intensityLabel' | 'aromaLabel' | 'bodyLabel' | 'coolingLabel'
>

type FlavourValues = Partial<Record<FlavourKey, number | string>>

type Args = Partial<FlavourLabels> & FlavourValues

type FlavourProfile = NonNullable<FlavourProfilesProps['profiles']>[number]

const normalizeValue = (value?: number | string): number | undefined => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) {
      return undefined
    }
    const parsed = parseInt(trimmed, 10)
    return Number.isNaN(parsed) ? undefined : parsed
  }

  return undefined
}

const buildProfile = (
  label: FlavourProfile['label'],
  value: number | string | undefined
): FlavourProfile => ({
  label,
  value: normalizeValue(value),
  scale: FLAVOUR_SCALE
})

const hasValidValue = (
  profile: FlavourProfile
): profile is FlavourProfile & { value: number } =>
  typeof profile.value === 'number'

export const getFlavourBars = ({
  intensityLabel,
  intensity,
  aromaLabel,
  aroma,
  bodyLabel,
  body,
  coolingLabel,
  cooling
}: Args): FlavourProfilesProps['profiles'] =>
  [
    buildProfile(intensityLabel, intensity),
    buildProfile(aromaLabel, aroma),
    buildProfile(bodyLabel, body),
    buildProfile(coolingLabel, cooling)
  ].filter(hasValidValue)
